/**
 * @fileoverview Home page route for the Onicorn application
 * @module routes/index
 *
 * Uses non-blocking auth pattern - the landing page renders immediately
 * and authenticated users are redirected client-side after auth resolves.
 */

import type { ReactElement } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  ScatteredStars,
  HeroBackgroundShape,
  HeroGreenBlur,
  HeroSecurityBadge,
  HeroTitleSection,
  HeroCTASection,
} from '@/features/homepage/components/hero';
import { useHomepageHoverStates } from '@/features/homepage/hooks/useHomepageHoverStates';
import {
  HowItWorksHeader,
  HowItWorksColumn,
  COLUMN_DATA,
  HowItWorksThemeColors,
  useColumnAnimation,
} from '@/features/homepage/components/how-it-works';
import {
  RefundGuaranteeHeader,
  RefundGuaranteeSection,
} from '@/features/homepage/components/refund-guarantee';
import { EcosystemHeader, EcosystemCards } from '@/features/homepage/components/ecosystem';
import { ExchangeLogosCarousel } from '@/features/homepage/components';
import type { CardPosition } from '@/features/homepage/components/ecosystem/types/EcosystemCardTypes';
import { Footer } from '@/components/layout/Footer';
import { useEnhancedAuth } from '@/lib/auth';
import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { saveManagerReferral } from '@/features/kol-managers';

/**
 * Search params interface for the homepage.
 */
interface HomeSearchParams {
  /** Action param to trigger specific behaviors (e.g., 'list-project' shows login modal) */
  action?: string;
  /** Manager referral code for KOL signup flow */
  manager_ref?: string;
}

/**
 * Home page component.
 *
 * Displays the main landing page with security badge and welcome content.
 * Features the SecurityBadge component prominently at the top to establish
 * trust and credibility with users.
 *
 * Uses the new refactored How It Works components with proper React state
 * management instead of direct DOM manipulation.
 *
 * Implements authentication-aware card actions:
 * - "Explore Deals" → Navigate to /deals
 * - "List your Project" / "Join as KOL" → Show login modal if not authenticated
 *
 * @component
 */
function HomePage(): ReactElement {
  const navigate = useNavigate();
  const search = useSearch({ from: '/' }) as HomeSearchParams;
  const { isAuthenticated, loading, onboardingCompleted } = useEnhancedAuth();
  const { setShowAuthFlow } = useDynamicContext();
  const hasRedirected = useRef(false);

  // Loading state for "Get Started" button on mobile
  const [isGetStartedLoading, setIsGetStartedLoading] = useState(false);

  // Clear loading state when authenticated
  useEffect(() => {
    if (isAuthenticated && isGetStartedLoading) {
      setIsGetStartedLoading(false);
    }
  }, [isAuthenticated, isGetStartedLoading]);

  // Timeout fallback to clear loading state (in case modal fails to open)
  useEffect(() => {
    if (isGetStartedLoading) {
      const timeout = setTimeout(() => {
        setIsGetStartedLoading(false);
      }, 5000); // 5 second fallback
      return () => clearTimeout(timeout);
    }
  }, [isGetStartedLoading]);

  // Get hover states - needed before the effect that uses setIsButtonHovered
  const {
    isButtonHovered,
    setIsButtonHovered,
    hoveredCard,
    setHoveredCard,
  } = useHomepageHoverStates();

  // Listen for Dynamic auth flow close to clear loading and hover states
  // We don't clear on open because the modal covers the button anyway
  useEffect(() => {
    const handleAuthFlowClose = (): void => {
      // Modal closed (user dismissed or completed) - ensure states are cleared
      setIsGetStartedLoading(false);
      setIsButtonHovered(false);
    };

    window.addEventListener('dynamic-auth-flow-close', handleAuthFlowClose);

    return () => {
      window.removeEventListener('dynamic-auth-flow-close', handleAuthFlowClose);
    };
  }, [setIsButtonHovered]);

  // Redirect authenticated users to appropriate page (non-blocking)
  // This replaces the blocking beforeLoad check
  useEffect(() => {
    // Only process once loading is complete and we haven't redirected yet
    if (loading || hasRedirected.current) {
      return;
    }

    if (isAuthenticated) {
      hasRedirected.current = true;
      if (onboardingCompleted) {
        navigate({ to: '/dashboard', replace: true });
      } else {
        navigate({ to: '/onboarding', replace: true });
      }
    }
  }, [loading, isAuthenticated, onboardingCompleted, navigate]);

  // Check for action query param to trigger Dynamic auth flow (e.g., from Footer "List Project" link)
  useEffect(() => {
    if (search.action === 'list-project' && !isAuthenticated && !loading) {
      setShowAuthFlow(true);
      // Clear the URL param to prevent modal re-showing on refresh
      navigate({ to: '/', search: {}, replace: true });
    }
  }, [search.action, isAuthenticated, loading, navigate, setShowAuthFlow]);

  // Check for manager_ref param (KOL Manager referral link)
  // Save to localStorage for use during onboarding
  useEffect(() => {
    if (search.manager_ref) {
      saveManagerReferral(search.manager_ref);
      // Open Dynamic auth flow for unauthenticated users
      if (!isAuthenticated && !loading) {
        setShowAuthFlow(true);
      }
      // Clear the URL param to keep URL clean
      navigate({ to: '/', search: {}, replace: true });
    }
  }, [search.manager_ref, isAuthenticated, loading, navigate, setShowAuthFlow]);

  // Column animations using the new hooks (replaces DOM manipulation)
  const column1Animation = useColumnAnimation({ themeColors: HowItWorksThemeColors.purple });
  const column2Animation = useColumnAnimation({ themeColors: HowItWorksThemeColors.green });
  const column3Animation = useColumnAnimation({ themeColors: HowItWorksThemeColors.orange });

  /**
   * Handles ecosystem card button clicks with authentication awareness.
   * Stores onboarding intent in localStorage for intent-based onboarding flow.
   * Opens Dynamic auth modal for unauthenticated users.
   *
   * @param cardId - The position identifier of the clicked card
   */
  const handleCardClick = (cardId: CardPosition): void => {
    switch (cardId) {
      case 'left': // "List your Project"
        if (isAuthenticated) {
          navigate({ to: '/dashboard' });
        } else {
          localStorage.setItem('onboarding_intent', 'project_creator');
          setShowAuthFlow(true);
        }
        break;

      case 'center': // "Join as KOL"
        if (isAuthenticated) {
          navigate({ to: '/dashboard' });
        } else {
          localStorage.setItem('onboarding_intent', 'kol');
          setShowAuthFlow(true);
        }
        break;

      case 'right': // "Explore Deals"
        if (isAuthenticated) {
          navigate({ to: '/deals' });
        } else {
          localStorage.setItem('onboarding_intent', 'regular');
          setShowAuthFlow(true);
        }
        break;

      default:
    }
  };


  // Authenticated users are redirected via useEffect above.
  // Return empty container briefly during redirect to avoid showing landing page.
  if (isAuthenticated && !loading) {
    return <div className="min-h-screen bg-onicorn-black" />;
  }

  return (
    <div className="min-h-screen bg-onicorn-black relative pt-[80px]">
      {/* Hero Background Shape - positioned behind all content */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <HeroBackgroundShape />

        {/* Scattered Stars Background */}
        <ScatteredStars />
      </div>

      {/* Hero Section */}
      <main className="mx-4 md:mx-[70px] pt-[44px] pb-4 md:pb-16 relative z-10">
        <div className="text-center relative overflow-visible min-h-[450px] md:min-h-0">
          {/* Security Badge - lowest layer */}
          <HeroSecurityBadge />

          {/* Green blur backdrop for title and subtitle */}
          <HeroGreenBlur />

          {/* Main Heading and Subheading */}
          <HeroTitleSection />

          {/* Get Started Button and Background Elements */}
          <HeroCTASection
            isButtonHovered={isButtonHovered}
            setIsButtonHovered={setIsButtonHovered}
            loading={isGetStartedLoading}
            onClick={() => {
              // Show loading state immediately for instant feedback on mobile
              setIsGetStartedLoading(true);
              // Trigger Dynamic auth flow
              setShowAuthFlow(true);
            }}
          />
        </div>
      </main>

      {/* Section 2: How Onicorn Works */}
      <section className="relative z-10 pb-11">
        <div>
          <HowItWorksHeader />

          {/* Three Column Cards - Refactored with composition pattern */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-8 mt-8 px-4 mx-auto max-w-full">
              {/* First Column - Projects (Purple Theme) */}
              <HowItWorksColumn
                data={COLUMN_DATA.first}
                animationState={column1Animation.animationState}
                hoverHandlers={column1Animation.hoverHandlers}
                style={column1Animation.containerStyles}
              />

              {/* Second Column - KOLs (Green Theme) */}
              <HowItWorksColumn
                data={COLUMN_DATA.second}
                animationState={column2Animation.animationState}
                hoverHandlers={column2Animation.hoverHandlers}
                style={column2Animation.containerStyles}
              />

              {/* Third Column - Community (Orange Theme) */}
              <div className="md:col-span-2 lg:col-span-1 flex justify-center w-full">
                <HowItWorksColumn
                  data={COLUMN_DATA.third}
                  animationState={column3Animation.animationState}
                  hoverHandlers={column3Animation.hoverHandlers}
                  style={column3Animation.containerStyles}
                />
              </div>
          </div>

          {/* Exchange Logos Carousel */}
          <ExchangeLogosCarousel className="mt-12" />
        </div>
      </section>

      {/* Mobile separator between How It Works and Refund Guarantee */}
      <div className="md:hidden h-px bg-[#282828] mx-4" />

      {/* Section 3: 100% Refund Guarantee */}
      <section className="relative z-10">
        {/* Top border - desktop only */}
        <div
          className="absolute top-0 h-px bg-[#282828] hidden md:block"
          style={{
            left: 'calc(50% - 650px)',
            right: 'calc(50% - 650px)',
          }}
        />
        <div className="mx-4 md:mx-[70px] pt-8 md:pt-[40px] overflow-visible">
          <RefundGuaranteeHeader />

          {/* Refactored Refund Guarantee Section with composition pattern */}
          <div className="relative flex justify-center" style={{ marginTop: '32px' }}>
            <RefundGuaranteeSection />
          </div>
        </div>
      </section>

      {/* Mobile separator between Refund Guarantee and Ecosystem */}
      <div className="md:hidden h-px bg-[#282828] mx-4 mt-8" />

      {/* Section 4: Join the Onicorn Ecosystem Section */}
      <section className="w-full flex justify-center md:pt-16">
        <div className="flex flex-col items-center">
          <EcosystemHeader />
          <EcosystemCards
            hoveredCard={hoveredCard}
            setHoveredCard={setHoveredCard}
            onButtonClick={handleCardClick}
          />
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

/**
 * Home route configuration.
 *
 * Defines the index route ('/') that serves as the application's
 * main landing page.
 *
 * No beforeLoad is used - authentication redirects are handled
 * client-side in the HomePage component for non-blocking performance.
 * This allows the landing page to render immediately while auth resolves.
 */
export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): HomeSearchParams => {
    return {
      action: typeof search.action === 'string' ? search.action : undefined,
      manager_ref: typeof search.manager_ref === 'string' ? search.manager_ref : undefined,
    };
  },
  component: HomePage,
});
